import { useEffect, useState } from "react";
import { Scissors, Loader2, Search } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const fmtDate = (d) => (d ? d.split("-").reverse().join("/") : "—");
const hasWork = (a) => (a?.sections || []).some((s) => s.rows_matched > 0 || !s.child_exists);

/**
 * Separa seções de OUTRO local (ex.: "PROCEDIMENTO REALIZADO NO CDE") de
 * mapas brutos já importados. Processa UM mapa por requisição (reler/parsear
 * PDF é lento) com barra de progresso — nunca estoura o tempo do servidor.
 * `rawMapId` → modo mapa único; sem → em lote a partir de uma data.
 */
export default function SplitSectionsDialog({ open, onOpenChange, rawMapId = null, onDone }) {
  const [dateFrom, setDateFrom] = useState("2026-09-01");
  const [items, setItems] = useState(null);      // análises com seção
  const [empty, setEmpty] = useState(null);      // análise vazia (modo único) p/ hint
  const [progress, setProgress] = useState(null); // {done,total,label,phase}
  const [errors, setErrors] = useState([]);
  const busy = !!progress;

  useEffect(() => { if (!open) { setItems(null); setEmpty(null); setProgress(null); setErrors([]); } }, [open]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { if (open && rawMapId) analyze(); }, [open, rawMapId]);

  async function analyzeOne(id) {
    const r = await api.post(`/raw-maps/${id}/split-sections`, null, { params: { dry_run: true } });
    return r.data;
  }

  async function analyze() {
    setErrors([]); setItems(null); setEmpty(null);
    try {
      const cands = rawMapId
        ? [{ id: rawMapId }]
        : (await api.get("/raw-maps/split-sections/candidates", { params: { date_from: dateFrom } })).data;
      const found = []; const errs = [];
      for (let i = 0; i < cands.length; i++) {
        const c = cands[i];
        setProgress({ phase: "Analisando", done: i, total: cands.length, label: c.source_date ? `${fmtDate(c.source_date)} · ${c.hospital_name || ""}` : "" });
        try {
          const a = await analyzeOne(c.id);
          if (hasWork(a)) found.push(a); else if (rawMapId) setEmpty(a);
        } catch (e) { errs.push(`${fmtDate(c.source_date)}: ${formatApiError(e.response?.data?.detail)}`); }
      }
      setItems(found); setErrors(errs);
      if (!rawMapId && found.length === 0) setEmpty({ scanned: cands.length });
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setProgress(null); }
  }

  async function commit() {
    const results = []; const errs = [];
    for (let i = 0; i < items.length; i++) {
      const m = items[i];
      setProgress({ phase: "Separando", done: i, total: items.length, label: `${fmtDate(m.source_date)} · ${m.hospital_name || ""}` });
      try {
        const r = await api.post(`/raw-maps/${m.raw_map_id}/split-sections`, null, { params: { dry_run: false } });
        results.push(r.data);
      } catch (e) { errs.push(`${fmtDate(m.source_date)}: ${formatApiError(e.response?.data?.detail)}`); }
    }
    setProgress(null);
    const moved = results.reduce((a, x) => a + (x.sections || []).reduce((b, s) => b + (s.rows_moved || 0), 0), 0);
    const created = results.reduce((a, x) => a + (x.sections || []).filter((s) => s.filtered_map_id).length, 0);
    const fixed = results.filter((x) => x.fixed_main_hospital).length;
    if (results.length) toast.success(`${results.length} mapa(s) separado(s): ${moved} linha(s) para o novo local, ${created} mapa(s) filtrado(s) criado(s) para validação${fixed ? `, ${fixed} hospital(is) de mapa corrigido(s)` : ""}.`, { duration: 9000 });
    if (errs.length) { setErrors(errs); toast.error(`${errs.length} mapa(s) com erro — veja a lista no diálogo.`); }
    onDone?.(results);
    if (!errs.length) onOpenChange(false);
    else setItems((cur) => cur.filter((m) => !results.some((r) => r.raw_map_id === m.raw_map_id)));
  }

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!busy) onOpenChange(o); }}>
      <DialogContent className="max-w-3xl max-h-[90vh] flex flex-col overflow-hidden" data-testid="split-sections-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Scissors className="h-5 w-5" /> Separar seções de outro local</DialogTitle>
          <DialogDescription>
            Relê o PDF guardado, move as linhas da seção (ex.: CDE) para um mapa bruto próprio daquele hospital,
            remove essas linhas e seus casos do mapa filtrado principal e cria o mapa filtrado do novo local para você validar
            (casos só serão gerados quando você clicar em "Gerar casos").
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 overflow-y-auto flex-1 min-h-0 pr-1">
          {!rawMapId && (
            <div className="flex items-end gap-3">
              <div className="space-y-1.5">
                <Label>Mapas a partir de</Label>
                <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} data-testid="split-date-from" />
              </div>
              <Button variant="outline" onClick={analyze} disabled={busy} data-testid="split-analyze">
                {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Search className="h-4 w-4 mr-2" />} Analisar
              </Button>
            </div>
          )}

          {progress && (
            <div className="space-y-1" data-testid="split-progress">
              <div className="flex justify-between text-xs text-slate-600">
                <span>{progress.phase} {progress.done + 1}/{progress.total} {progress.label && `— ${progress.label}`}</span>
                <span>{Math.round((progress.done / Math.max(progress.total, 1)) * 100)}%</span>
              </div>
              <div className="h-2 bg-slate-100 rounded overflow-hidden">
                <div className="h-full bg-blue-600 transition-[width]" style={{ width: `${(progress.done / Math.max(progress.total, 1)) * 100}%` }} />
              </div>
            </div>
          )}

          {items && items.length === 0 && !progress && (
            <div className="text-sm text-slate-600 bg-slate-50 border rounded-md p-3" data-testid="split-empty">
              Nenhuma seção de outro hospital encontrada{empty?.scanned != null ? ` (${empty.scanned} mapa(s) PDF verificados)` : ""}.
              {empty?.hint && <div className="mt-1 text-amber-700">{empty.hint}</div>}
              {empty && Object.keys(empty.unresolved || {}).length > 0 && (
                <div className="mt-1 text-amber-700">
                  Seções sem hospital cadastrado: {Object.entries(empty.unresolved).map(([l, n]) => `${l} (${n})`).join(", ")} —
                  confira em <strong>Hospitais</strong> se o local está cadastrado, <strong>ativo</strong> e com a sigla igual à seção (ex.: CDE), e analise de novo.
                </div>
              )}
              {empty && (empty.sections || []).length > 0 && !hasWork(empty) && (
                <div className="mt-1 text-slate-600" data-testid="split-already-done">
                  A seção {empty.sections.map((s) => s.location).join(", ")} já tem mapa próprio neste dia e nenhuma linha dela permanece neste mapa — nada a fazer.
                </div>
              )}
            </div>
          )}

          {items && items.length > 0 && (
            <table className="w-full text-sm" data-testid="split-table">
              <thead className="text-xs uppercase text-slate-500 border-b">
                <tr><th className="text-left py-1.5">Mapa</th><th className="text-left">Seção → hospital</th><th className="text-right">Linhas</th><th className="text-right">No filtrado</th><th className="text-right">Casos</th></tr>
              </thead>
              <tbody>
                {items.map((m) => m.sections.map((s, i) => (
                  <tr key={`${m.raw_map_id}-${i}`} className="border-b border-slate-100 align-top" data-testid={`split-row-${m.raw_map_id}-${i}`}>
                    <td className="py-1.5">
                      {fmtDate(m.source_date)} · {m.hospital_name || "sem hospital"}
                      {m.fix_main_hospital && (
                        <div className="text-[11px] text-sky-700" data-testid={`split-fix-hospital-${m.raw_map_id}`}>
                          hospital do mapa será corrigido para <strong>{m.fix_main_hospital.hospital_name}</strong> (cabeçalho do PDF)
                        </div>
                      )}
                    </td>
                    <td><span className="font-mono text-xs bg-slate-100 rounded px-1">{s.location}</span> → {s.hospital_name}</td>
                    <td className="text-right">
                      {s.rows_matched}/{s.rows_in_pdf}
                      {s.rows_matched === 0 && !s.child_exists && (
                        <div className="text-[11px] text-amber-700 text-right" data-testid={`split-from-pdf-${m.raw_map_id}-${i}`}>
                          linhas já removidas do mapa — será criado a partir do PDF
                        </div>
                      )}
                    </td>
                    <td className="text-right">{s.filtered_rows_affected}</td>
                    <td className="text-right">{s.cases_affected}</td>
                  </tr>
                )))}
              </tbody>
            </table>
          )}

          {errors.length > 0 && (
            <ul className="text-xs text-red-700 bg-red-50 border border-red-200 rounded-md p-2 space-y-0.5" data-testid="split-errors">
              {errors.map((e, i) => <li key={i}>{e}</li>)}
            </ul>
          )}
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={busy}>Fechar</Button>
          <Button onClick={commit} disabled={busy || !items || items.length === 0} className="bg-blue-700 hover:bg-blue-800" data-testid="split-commit">
            {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Scissors className="h-4 w-4 mr-2" />} Separar agora
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
